import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useNotificationStore } from '../store/notification.store';
import { useAuthStore } from '../store/auth.store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    isSSEConnected,
    connectSSE, 
    disconnectSSE,
    fetchNotifications, 
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      fetchUnreadCount();
      connectSSE();
    }

    return () => {
      if (isAuthenticated) {
        disconnectSSE();
      }
    };
  }, [isAuthenticated]);

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    // Navigate based on notification type
    if (notification.type === 'friend_request' || notification.type === 'friend_accepted') {
      // Navigate with state to open the requests tab
      navigate('/friends', { state: { activeTab: 'requests' } });
    } else if (notification.type === 'secret_santa_invite' && notification.data?.group_permalink) {
      // Navigate to secret santa group page
      navigate(`/secret-santa/${notification.data.group_permalink}`);
    } else if (notification.type === 'new_message') {
      if (notification.data?.is_secret_santa && notification.data?.group_id) {
        // Check if it's anonymous (receiver role) or a reply (giver role)
        if (notification.data?.is_anonymous !== false) {
          // Anonymous message: navigate to receiver role
          navigate(`/messages?groupId=${notification.data.group_id}&role=receiver`);
        } else {
          // Reply: navigate to giver role (you're the giver receiving a reply)
          navigate(`/messages?groupId=${notification.data.group_id}&role=giver`);
        }
      } else if (notification.data?.conversation_user_id) {
        // Navigate to regular Messages tab with the sender
        navigate(`/messages/${notification.data.conversation_user_id}`);
      } else {
        // Fallback: just go to messages page
        navigate('/messages');
      }
    }
  };

  const formatNotificationMessage = (notification: any) => {
    switch (notification.type) {
      case 'friend_request':
        return `${notification.data?.from_user_name || 'Someone'} sent you a friend request`;
      case 'friend_accepted':
        return `${notification.data?.from_user_name || 'Someone'} accepted your friend request`;
      case 'wishlist_shared':
        return 'shared a wishlist with you';
      case 'secret_santa_invite':
        return `${notification.data?.from_user_name || 'Someone'} invites you to Secret Santa: ${notification.data?.group_name || 'a group'}`;
      case 'new_message':
        if (notification.data?.is_secret_santa) {
          // Check if it's anonymous (from Secret Santa) or a reply (from assigned recipient)
          if (notification.data?.is_anonymous !== false) {
            // Anonymous message from Secret Santa
            return 'Your Secret Santa has sent you a message';
          } else {
            // Reply from assigned recipient (not anonymous)
            return `${notification.data?.sender_name || 'Someone'} has sent you a message`;
          }
        } else {
          return `${notification.data?.sender_name || 'Someone'} has sent you a message`;
        }
      default:
        return 'sent you a notification';
    }
  };

  const recentNotifications = notifications.slice(0, 5);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <DropdownMenuLabel>
          <div className="flex items-center justify-between">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAllAsRead();
                }}
                className="h-auto p-0 text-xs"
              >
                Mark all as read
              </Button>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recentNotifications.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No notifications
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {recentNotifications.map((notification) => (
              <div key={notification.id}>
                <DropdownMenuItem
                  className="flex flex-col items-start py-3 cursor-pointer"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                      {formatNotificationMessage(notification)}
                    </span>
                    {!notification.read && (
                      <Badge variant="secondary" className="ml-2 h-2 w-2 rounded-full p-0" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground mt-1">
                    {new Date(notification.created_at).toLocaleDateString()}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;

